import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useEscKeyHandler from '../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { 
  Calendar, 
  Clock, 
  Phone, 
  Link, 
  User, 
  CheckCircle,
  X,
  Mail,
  Users,
  MessageSquare,
  Video,
  FileText,
  AlertCircle
} from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, parseISO } from 'date-fns';
import { tenantAdminAPI } from '../services/api/tenantAdminAPI';
import { getGenderFormValue, getGenderOptions } from '../utils/genderUtils';

const AddPatientModal = ({ onClose, onSubmit, patient = null, isEditing = false, currentTenantId = null, onScheduleCreated = null }) => {

  const [formData, setFormData] = useState({
    first_name: patient?.first_name || '',
    last_name: patient?.last_name || '',
    email: patient?.email || '',
    phone: patient?.phone || '',
    dob: patient?.dob || '',
    gender: getGenderFormValue(patient?.gender),
    address: patient?.address || '',
    emergency_contact_name: patient?.emergency_contact_name || '',
    emergency_contact_phone: patient?.emergency_contact_phone || '',
    medical_notes: patient?.medical_notes || ''
  });

  // Phone number validation function
  const validatePhoneInput = (value) => {
    // Allow only numbers, spaces, +, and -
    return value.replace(/[^0-9\s+\-]/g, '');
  };
  const [saving, setSaving] = useState(false);
  const [internalTenantId, setInternalTenantId] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);

  // Schedule state (from SchedulingModal)
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [communicationMethod, setCommunicationMethod] = useState('link');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // React Hook Form for scheduling
  const { register: registerSchedule, handleSubmit: handleScheduleSubmit, formState: { errors: scheduleErrors }, reset: resetSchedule, setValue: setScheduleValue, getValues } = useForm({
    defaultValues: {
      title: '',
      description: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      time: '09:00',
      communication_method: 'link',
      notes: ''
    }
  });

  // Set current tenant ID from prop
  useEffect(() => {
    console.log('AddPatientModal: currentTenantId prop received:', currentTenantId);
    if (currentTenantId) {
      setInternalTenantId(currentTenantId);
      console.log('AddPatientModal: internalTenantId set to:', currentTenantId);
    }
  }, [currentTenantId]);

  // Update form data when patient prop changes (for editing mode)
  useEffect(() => {
    if (patient && isEditing) {
      setFormData({
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        email: patient.email || '',
        phone: patient.phone || '',
        dob: patient.dob || '',
        gender: getGenderFormValue(patient.gender),
        address: patient.address || '',
        emergency_contact_name: patient.emergency_contact_name || '',
        emergency_contact_phone: patient.emergency_contact_phone || '',
        medical_notes: patient.medical_notes || ''
      });
    }
  }, [patient, isEditing]);

  // Add Esc key handler for modal
  useEscKeyHandler(true, onClose);

  // Generate calendar days
  const calendarDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const timeSlots = [
    '06:00','07:00','08:00','09:00','10:00','11:00',
    '12:00','13:00','14:00','15:00',
    '16:00','17:00','18:00','19:00', '20:00','21:00','22:00',
  ];

  const communicationMethods = [
    { id: 'link', label: 'Send Link / SMS', icon: Link, color: 'text-purple-600' },
    { id: 'call', label: 'Phone Call', icon: Phone, color: 'text-blue-600' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('=== ADD PATIENT MODAL: Form Submission Start ===');
      console.log('AddPatientModal: Form submitted with data:', formData);
      console.log('AddPatientModal: currentTenantId prop:', currentTenantId);
      console.log('AddPatientModal: isEditing prop:', isEditing);
      
      setSaving(true);
      
      // Validate required fields
      if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim() || !formData.phone.trim()) {
        console.error('AddPatientModal: Validation failed - missing required fields');
        toast.error('Please fill in all required fields (First Name, Last Name, Email, Phone)');
        return;
      }

      if (!currentTenantId) {
        console.error('AddPatientModal: Validation failed - missing tenant ID');
        toast.error('Tenant ID is missing. Please refresh the page and try again.');
        return;
      }

      // Add tenant_id and create alias from first_name and last_name
      const patientData = {
        alias: `${formData.first_name} ${formData.last_name}`.trim(),
        tenant_id: currentTenantId,
        // Map form fields to backend expected fields
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        dob: formData.dob && formData.dob.trim() !== '' ? formData.dob : null,
        gender: formData.gender || null,
        address: formData.address || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        medical_notes: formData.medical_notes || null
      };

      // Always use tenant_id for both create and update operations
      // The backend expects tenant_id for patient creation
      patientData.tenant_id = currentTenantId;
      console.log('AddPatientModal: Using tenant_id for patient operation:', currentTenantId);

      // If editing, include the patient ID
      if (isEditing && patient?.id) {
        patientData.id = patient.id;
        console.log('AddPatientModal: Editing mode - including patient ID:', patient.id);
      }
      
      // Debug: Log the data being sent
      console.log('AddPatientModal: Final patient data being sent:', patientData);
      console.log('AddPatientModal: Current tenant ID:', currentTenantId);
      console.log('AddPatientModal: Form data:', formData);
      
      // Validate required fields before sending
      if (!patientData.alias || !patientData.email || !patientData.phone || !patientData.tenant_id) {
        console.error('AddPatientModal: Missing required fields:', {
          alias: patientData.alias,
          email: patientData.email,
          phone: patientData.phone,
          tenant_id: patientData.tenant_id
        });
        toast.error('Missing required fields. Please check your input.');
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(patientData.email)) {
        console.error('AddPatientModal: Invalid email format:', patientData.email);
        toast.error('Please enter a valid email address.');
        return;
      }
      
      // Check if email already exists (only for new patients)
      if (!isEditing) {
        try {
          console.log('AddPatientModal: Checking if email already exists:', patientData.email);
          const existingPatients = await tenantAdminAPI.getPatients();
          const emailExists = existingPatients.data.some(patient => 
            patient.email && patient.email.toLowerCase() === patientData.email.toLowerCase()
          );
          
          if (emailExists) {
            console.error('AddPatientModal: Email already exists:', patientData.email);
            toast.error('A patient with this email already exists. Please use a different email.');
            return;
          }
        } catch (error) {
          console.warn('AddPatientModal: Could not check for existing emails:', error);
          // Continue with creation - let backend handle the validation
        }
      }
      
      console.log('AddPatientModal: About to call onSubmit callback');
      
      // Always use onSubmit callback - let parent component handle API calls
      const createdPatient = await onSubmit(patientData);
      console.log('AddPatientModal: onSubmit callback completed successfully');
      console.log('AddPatientModal: Created patient data received:', createdPatient);
      console.log('AddPatientModal: Created patient type:', typeof createdPatient);
      console.log('AddPatientModal: Created patient keys:', Object.keys(createdPatient || {}));
      
      // If scheduling is enabled and has data, create the schedule
      if (showSchedule && selectedTimes.length > 0) {
        try {
          console.log('AddPatientModal: Creating schedule for new patient');
          
          // Verify patient was created successfully before scheduling
          if (!createdPatient || !createdPatient.id) {
            console.error('AddPatientModal: Patient creation failed or no patient ID returned');
            toast.error('Patient creation failed. Cannot create schedule.');
            return;
          }
          
          console.log('AddPatientModal: Patient created successfully with ID:', createdPatient.id);
          
          // Add a delay to ensure patient is fully processed in the database
          console.log('AddPatientModal: Waiting 2 seconds before creating schedule...');
          toast.loading('Creating schedule for new patient...', { duration: 2000 });
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Verify patient exists in database before creating schedule
          try {
            console.log('AddPatientModal: Verifying patient exists in database...');
            const patientsResponse = await tenantAdminAPI.getPatients();
            const patientExists = patientsResponse.data.some(patient => patient.id === createdPatient.id);
            
            if (!patientExists) {
              console.error('AddPatientModal: Patient not found in database after creation');
              toast.error('Patient not found in database. Cannot create schedule.');
              return;
            }
            
            console.log('AddPatientModal: Patient verified in database, proceeding with schedule creation');
          } catch (error) {
            console.warn('AddPatientModal: Could not verify patient in database:', error);
            // Continue with schedule creation - let the API handle validation
          }
          
          // Get schedule form data
          const scheduleFormData = getValues();
          
          // Create schedule with the new patient ID
          const scheduleResult = await handleScheduleFormSubmit(scheduleFormData, createdPatient?.id);
          
          if (scheduleResult && onScheduleCreated) {
            console.log('AddPatientModal: Notifying parent of schedule creation');
            onScheduleCreated(scheduleResult);
          }
          
          if (scheduleResult) {
            console.log('AddPatientModal: Schedule created successfully');
            toast.success('Patient and schedule created successfully!');
          }
        } catch (scheduleError) {
          console.error('AddPatientModal: Schedule creation failed:', scheduleError);
          toast.error('Patient created but schedule failed. You can create schedule manually.');
        }
      } else {
        console.log('AddPatientModal: Patient created successfully - no schedule to create');
        toast.success('Patient created successfully!');
      }
      
        // Close modal and reset form
        onClose();
        console.log('AddPatientModal: Modal closed and form reset');
        console.log('=== ADD PATIENT MODAL: Form Submission End ===');
      
    } catch (error) {
      console.error('=== ADD PATIENT MODAL: Form Submission Error ===');
      console.error('AddPatientModal: Error during form submission:', error);
      console.error('AddPatientModal: Error message:', error.message);
      console.error('AddPatientModal: Error stack:', error.stack);
      console.error('=== END ERROR DETAILS ===');
      
      toast.error(`Failed to submit form: ${error.message}`);
    } finally {
      setSaving(false);
      console.log('AddPatientModal: Saving state set to false');
    }
  };

  // Function to check if a time slot is disabled (already passed)
  const isTimeDisabled = (time) => {
    const today = new Date();
    const selectedDateOnly = new Date(selectedDate);
    selectedDateOnly.setHours(0, 0, 0, 0);
    const todayOnly = new Date(today);
    todayOnly.setHours(0, 0, 0, 0);
    
    // If the selected date is in the past, all times are disabled
    if (selectedDateOnly < todayOnly) {
      return true;
    }
    
    // If selecting today's date, check if the time has passed
    if (selectedDateOnly.getTime() === todayOnly.getTime()) {
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
      const [hours, minutes] = time.split(':').map(Number);
      const scheduledTime = hours * 60 + minutes;
      
      return scheduledTime <= currentTime;
    }
    
    return false; // Future dates are not disabled
  };

  const resetScheduleForm = () => {
    setSelectedTimes([]);
    setSelectedDate(new Date());
    setCommunicationMethod('link');
    setShowSchedule(false);
    resetSchedule();
  };

  const isDateDisabled = (date) => {
    // Disable past dates
    return date < new Date().setHours(0, 0, 0, 0);
  };

  // Schedule submission handler (from SchedulingModal)
  const handleScheduleFormSubmit = async (scheduleData, patientId = null) => {
    try {
      setScheduleLoading(true);
      
      if (selectedTimes.length === 0) {
        toast.error('Please select at least one time slot');
        return;
      }

      if (!patientId) {
        toast.error('Patient ID is required for scheduling');
        return;
      }

      // For each selected time, schedule an email
      const scheduledJobs = [];
      
      for (const time of selectedTimes) {
        // Ensure time format is HH:MM (remove seconds if present)
        const formattedTime = time.includes(':') ? time.substring(0, 5) : time;
        
        const schedulePayload = {
          patient_id: patientId,
          scheduled_date: format(selectedDate, 'yyyy-MM-dd'),
          scheduled_time: formattedTime,
          title: scheduleData.title,
          description: scheduleData.description,
          notes: scheduleData.notes
        };
        
        console.log(`AddPatientModal: Creating schedule payload for time ${time}:`, schedulePayload);

        try {
          const response = await tenantAdminAPI.scheduleRecordingEmail(schedulePayload);
          scheduledJobs.push(response.data);
        } catch (error) {
          console.error(`Failed to schedule email for time ${time}:`, error);
          console.error(`Error response:`, error.response?.data);
          console.error(`Error status:`, error.response?.status);
          console.error(`Error detail:`, error.response?.data?.detail);
          console.error(`Schedule payload:`, schedulePayload);
          toast.error(`Failed to schedule email for ${time}: ${error.response?.data?.detail || error.message}`);
        }
      }

      if (scheduledJobs.length > 0) {
        toast.success(`Successfully scheduled ${scheduledJobs.length} recording email(s)!`);
        return {
          ...scheduleData,
          date: format(selectedDate, 'yyyy-MM-dd'),
          times: selectedTimes,
          communication_method: communicationMethod,
          scheduled_jobs: scheduledJobs
        };
      }
    } catch (error) {
      console.error('Failed to create schedule:', error);
      toast.error('Failed to create schedule');
      throw error;
    } finally {
      setScheduleLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#F9FBFC] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#DCEAF5]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-[#0B1C2C]">
              {isEditing ? 'Edit Patient' : 'Add New Patient'}
            </h2>
            <button
              onClick={onClose}
              className="text-[#3D3D3D] hover:text-[#207CE5]"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Patient Form Section */}
          <div>
            <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Patient Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: validatePhoneInput(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  placeholder="e.g., +1 555-123-4567"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Gender
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                >
                  {getGenderOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Emergency Contact Name (Optional)
                </label>
                <input
                  type="text"
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Emergency Contact Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_phone: validatePhoneInput(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  placeholder="e.g., +1 555-123-4567"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                Medical Notes
              </label>
              <textarea
                value={formData.medical_notes}
                onChange={(e) => setFormData({ ...formData, medical_notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                placeholder="Any relevant medical information..."
              />
            </div>
          </div>

          {/* Schedule Toggle Button */}
          <div className="border-t border-[#DCEAF5] pt-6">
            <button
              type="button"
              onClick={() => setShowSchedule(!showSchedule)}
              className={`w-full py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                showSchedule 
                  ? 'bg-[#207CE5] text-white hover:bg-[#4682B4]' 
                  : 'bg-[#DCEAF5] text-[#3D3D3D] hover:bg-[#207CE5] hover:text-white'
              }`}
            >
              <Calendar className="h-4 w-4" />
              <span>{showSchedule ? 'Remove Scheduling' : 'Add Appointment Scheduling'}</span>
            </button>
          </div>

          {/* Schedule Section - Only show when enabled */}
          {showSchedule && (
            <div className="border-t border-[#DCEAF5] pt-6">
              <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Appointment Scheduling</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Calendar and Time */}
                <div className="space-y-6">
                  {/* Calendar */}
                  <div className="bg-[#DCEAF5] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-[#0B1C2C]">Select Date</h4>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setCurrentMonth(addDays(currentMonth, -30))}
                          className="p-1 hover:bg-[#207CE5] hover:text-white rounded transition-colors"
                        >
                          ←
                        </button>
                        <span className="text-sm font-medium text-[#0B1C2C]">
                          {format(currentMonth, 'MMMM yyyy')}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCurrentMonth(addDays(currentMonth, 30))}
                          className="p-1 hover:bg-[#207CE5] hover:text-white rounded transition-colors"
                        >
                          →
                        </button>
                      </div>
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-xs font-medium text-[#3D3D3D] py-2">
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                      {calendarDays.map((day, index) => {
                        const isDisabled = isDateDisabled(day);
                        const isSelected = isSameDay(day, selectedDate);
                        const isCurrentDay = isToday(day);
                        
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => !isDisabled && setSelectedDate(day)}
                            disabled={isDisabled}
                            className={`
                              p-2 text-sm rounded-lg transition-colors
                              ${isDisabled 
                                ? 'text-[#3D3D3D] opacity-30 cursor-not-allowed' 
                                : isSelected
                                ? 'bg-[#207CE5] text-white'
                                : isCurrentDay
                                ? 'bg-[#207CE5] bg-opacity-20 text-[#207CE5] hover:bg-opacity-30'
                                : 'hover:bg-[#207CE5] hover:bg-opacity-10'
                              }
                            `}
                          >
                            {format(day, 'd')}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Selection */}
                  <div className="bg-[#DCEAF5] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-[#0B1C2C]">Select Time(s)</h4>
                      <div className="flex items-center space-x-2 text-sm text-[#3D3D3D]">
                        <Clock className="h-4 w-4" />
                        <span>Indian Standard Time (IST)</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.map(time => {
                        const isDisabled = isTimeDisabled(time);
                        const isSelected = selectedTimes.includes(time);
                        
                        return (
                          <button
                            key={time}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => {
                              if (!isDisabled) {
                                setSelectedTimes(prev => 
                                  prev.includes(time)
                                    ? prev.filter(t => t !== time)
                                    : [...prev, time]
                                );
                              }
                            }}
                            className={`
                              p-2 text-sm rounded-lg border transition-colors
                              ${isDisabled
                                ? 'border-[#DCEAF5] bg-gray-100 text-gray-400 cursor-not-allowed'
                                : isSelected
                                  ? 'border-[#207CE5] bg-[#207CE5] text-white hover:bg-[#4682B4]'
                                  : 'border-[#DCEAF5] hover:border-[#207CE5] hover:bg-[#207CE5] hover:bg-opacity-10'
                              }
                            `}
                            title={isDisabled ? 'This time has already passed' : ''}
                          >
                            {time}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-sm text-[#3D3D3D]">
                      {selectedTimes.length > 0
                        ? `Selected: ${selectedTimes.join(', ')} (IST)`
                        : 'No time slots selected'}
                    </div>
                    {(() => {
                      const disabledCount = timeSlots.filter(time => isTimeDisabled(time)).length;
                      const totalCount = timeSlots.length;
                      const availableCount = totalCount - disabledCount;
                      
                      if (disabledCount > 0) {
                        return (
                          <div className="mt-1 text-xs text-[#3D3D3D] opacity-75">
                            {availableCount} of {totalCount} time slots available
                            {disabledCount > 0 && ` (${disabledCount} already passed)`}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {/* Right Column - Details and Communication */}
                <div className="space-y-6">
                  {/* Basic Details */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#3D3D3D] mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        {...registerSchedule('title', { required: 'Title is required' })}
                        className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                        placeholder="Appointment title"
                        defaultValue={`Recording Session - ${formData.first_name} ${formData.last_name}`}
                      />
                      {scheduleErrors.title && (
                        <p className="mt-1 text-sm text-red-600">{scheduleErrors.title.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#3D3D3D] mb-2">
                        Description
                      </label>
                      <textarea
                        {...registerSchedule('description')}
                        rows={3}
                        className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                        placeholder="Appointment description or notes"
                        defaultValue="Scheduled recording session for vital signs analysis"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#3D3D3D] mb-2">
                        Patient
                      </label>
                      <div className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg bg-[#F9FBFC] text-[#3D3D3D]">
                        {formData.first_name} {formData.last_name} (Auto-populated)
                      </div>
                    </div>
                  </div>

                  {/* Communication Method */}
                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-4">
                      Communication Method
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {communicationMethods.map(method => {
                        const Icon = method.icon;
                        return (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => setCommunicationMethod(method.id)}
                            className={`
                              p-4 border-2 rounded-lg transition-colors flex items-center space-x-3
                              ${communicationMethod === method.id
                                ? 'border-[#207CE5] bg-[#DCEAF5]'
                                : 'border-[#DCEAF5] hover:border-[#207CE5]'
                              }
                            `}
                          >
                            <Icon className={`h-5 w-5 ${method.color}`} />
                            <span className="text-sm font-medium">{method.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <label className="block text-sm font-medium text-[#3D3D3D] mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      {...registerSchedule('notes')}
                      rows={4}
                      className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                      placeholder="Any additional notes or instructions..."
                    />
                  </div>

                  {/* Summary */}
                  <div className="bg-[#DCEAF5] rounded-lg p-4">
                    <h4 className="text-sm font-medium text-[#0B1C2C] mb-2">Schedule Summary</h4>
                    <div className="space-y-1 text-sm text-[#3D3D3D]">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4" />
                        <span>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>{selectedTimes.length > 0 ? selectedTimes.join(', ') + ' (IST)' : 'No time selected'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4" />
                        <span>{formData.first_name} {formData.last_name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {React.createElement(communicationMethods.find(m => m.id === communicationMethod)?.icon, { className: "h-4 w-4" })}
                        <span>{communicationMethods.find(m => m.id === communicationMethod)?.label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-[#DCEAF5]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#3D3D3D] bg-[#F9FBFC] border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.first_name || !formData.last_name || !formData.phone || !formData.email}
              className="px-6 py-2 text-sm font-medium text-white bg-[#207CE5] border border-transparent rounded-lg hover:bg-[#4682B4] disabled:opacity-50 flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>
                    {isEditing 
                      ? 'Update Patient'
                      : showSchedule && selectedTimes.length > 0 
                        ? 'Create Patient & Schedule' 
                        : 'Create Patient'
                    }
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPatientModal;
